import React, { useState } from 'react';
import { Section } from '../components/ui/Section';
import { ArrowRight, Phone, Mail, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { sendEmail } from '../utils/emailService';
import { getContent } from '../utils/contentStore';
import { renderIcon } from '../utils/iconMap';
import { useSEO } from '../utils/useSEO';
import { useLanguage } from '../utils/i18n';
export function PartnerPage() {
  useSEO('partner');
  const {
    partnerBenefits: benefits,
    partnerSteps: steps,
    partnerPage: pg
  } = getContent();
  const {
    t,
    loc
  } = useLanguage();
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    phone: '',
    email: '',
    region: '',
    volume: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSubmitError(false);
    const result = await sendEmail({
      from_name: formData.name,
      phone: formData.phone,
      email: formData.email,
      company: formData.company,
      region: formData.region,
      volume: formData.volume,
      message: formData.message,
      form_type: 'Заявка партнёра BM PARTNER'
    });
    setLoading(false);
    if (result.ok) {
      setSubmitted(true);
    } else {
      setSubmitError(true);
    }
  };
  return <div className="pt-20" data-id="element-1039">
      {/* Hero */}
      <div className="bg-gray-900 text-white py-24 relative overflow-hidden" data-id="element-1040">
        <div className="absolute inset-0 opacity-20 pointer-events-none" data-id="element-1041">
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-red-600 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2" data-id="element-1042" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-red-800 rounded-full blur-[100px] translate-x-1/4 translate-y-1/4" data-id="element-1043" />
        </div>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10" data-id="element-1044">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} data-id="element-1045">
            <span className="text-gray-400 font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-1046">
              {loc(pg.heroLabel)}
            </span>
            <h1 className="font-heading text-5xl md:text-7xl font-bold uppercase mb-6 leading-tight" data-id="element-1047">
              {loc(pg.heroTitle)}{' '}
              <span className="text-red-500" data-id="element-1048">{loc(pg.heroTitleAccent)}</span>
            </h1>
            <p className="text-gray-300 text-xl max-w-2xl leading-relaxed" data-id="element-1049">
              {loc(pg.heroDescription)}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Benefits */}
      <Section className="bg-gray-50" data-id="element-1050">
        <div className="text-center mb-16" data-id="element-1051">
          <h2 className="text-gray-500 font-bold tracking-widest uppercase mb-3 text-sm" data-id="element-1052">
            {loc(pg.benefitsLabel)}
          </h2>
          <h3 className="font-heading text-4xl font-bold text-gray-900 uppercase leading-tight" data-id="element-1053">
            {loc(pg.benefitsTitle)}{' '}
            <span className="text-red-600" data-id="element-1054">{loc(pg.benefitsTitleAccent)}</span>
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1055">
          {benefits.map((b, i) => <motion.div key={b.id} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          delay: i * 0.05,
          duration: 0.4
        }} className="bg-white p-8 rounded-sm shadow-sm border-b-4 border-transparent hover:border-gray-800 hover:shadow-md transition-all duration-300 group" data-id="element-1056">
              <div className="text-gray-300 group-hover:text-gray-700 transition-colors mb-5" data-id="element-1057">
                {renderIcon(b.icon, 'h-8 w-8')}
              </div>
              <h4 className="font-heading text-xl font-bold text-gray-900 mb-3" data-id="element-1058">
                {loc(b.title)}
              </h4>
              <p className="text-gray-600 leading-relaxed text-sm" data-id="element-1059">
                {loc(b.description)}
              </p>
            </motion.div>)}
        </div>
      </Section>

      {/* Steps */}
      <Section dark data-id="element-1060">
        <div className="text-center mb-16" data-id="element-1061">
          <h2 className="text-gray-400 font-bold tracking-widest uppercase mb-3 text-sm" data-id="element-1062">
            {loc(pg.stepsLabel)}
          </h2>
          <h3 className="font-heading text-4xl font-bold text-white uppercase leading-tight" data-id="element-1063">
            {loc(pg.stepsTitle)}{' '}
            <span className="text-red-500" data-id="element-1064">{loc(pg.stepsTitleAccent)}</span>
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1065">
          {steps.map((step, i) => <motion.div key={step.id} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="relative" data-id="element-1066">
              <div className="text-white/30 font-heading text-6xl font-bold opacity-30 mb-4 leading-none" data-id="element-1067">
                {step.num}
              </div>
              <h4 className="font-bold text-white text-lg mb-2" data-id="element-1068">
                {loc(step.title)}
              </h4>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-1069">
                {loc(step.desc)}
              </p>
              {i < steps.length - 1 && <div className="hidden lg:block absolute top-8 right-0 translate-x-1/2" data-id="element-1070">
                  <ArrowRight className="h-6 w-6 text-white/20" data-id="element-1071" />
                </div>}
            </motion.div>)}
        </div>
      </Section>

      {/* Form */}
      <Section className="bg-white" data-id="element-1072">
        <div className="max-w-3xl mx-auto" data-id="element-1073">
          <div className="text-center mb-12" data-id="element-1074">
            <h2 className="text-gray-500 font-bold tracking-widest uppercase mb-3 text-sm" data-id="element-1075">
              {loc(pg.formLabel)}
            </h2>
            <h3 className="font-heading text-4xl font-bold text-gray-900 uppercase mb-4 leading-tight" data-id="element-1076">
              {loc(pg.formTitle)}{' '}
              <span className="text-red-600" data-id="element-1077">{loc(pg.formTitleAccent)}</span>
            </h3>
            <p className="text-gray-600" data-id="element-1078">{loc(pg.formDescription)}</p>
          </div>

          {submitted ? <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="text-center py-16 bg-gray-50 rounded-sm border border-gray-100" data-id="element-1079">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1080">
                <CheckCircle className="h-8 w-8 text-green-600" data-id="element-1081" />
              </div>
              <h4 className="font-heading text-2xl font-bold text-gray-900 mb-3" data-id="element-1082">
                {t('contact_form_success')}
              </h4>
              <p className="text-gray-600 max-w-md mx-auto" data-id="element-1083">
                {t('partner_success_desc')}
              </p>
            </motion.div> : <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-sm shadow-lg p-8 md:p-10 border-t-4 border-t-gray-900" data-id="element-1084">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1085">
                {[{
              name: 'company',
              label: t('partner_form_company') + ' *',
              type: 'text',
              required: true,
              placeholder: t('partner_form_placeholder_company')
            }, {
              name: 'name',
              label: t('partner_form_contact') + ' *',
              type: 'text',
              required: true,
              placeholder: t('partner_form_placeholder_name')
            }, {
              name: 'phone',
              label: t('contact_form_phone') + ' *',
              type: 'tel',
              required: true,
              placeholder: '+996 ___ __ __ __'
            }, {
              name: 'email',
              label: t('contact_email'),
              type: 'email',
              required: false,
              placeholder: 'example@mail.com'
            }, {
              name: 'region',
              label: t('partner_form_region'),
              type: 'text',
              required: false,
              placeholder: t('partner_form_placeholder_region')
            }].map(f => <div key={f.name} className="space-y-2" data-id="element-1086">
                    <label className="text-sm font-medium text-gray-700" data-id="element-1087">
                      {f.label}
                    </label>
                    <input type={f.type} name={f.name} required={f.required} value={(formData as any)[f.name]} onChange={handleChange} placeholder={f.placeholder} className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all" data-id="element-1088" />
                  </div>)}
                <div className="space-y-2" data-id="element-1089">
                  <label className="text-sm font-medium text-gray-700" data-id="element-1090">
                    {t('partner_form_volume')}
                  </label>
                  <select name="volume" value={formData.volume} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all bg-white" data-id="element-1091">
                    <option value="" data-id="element-1092">{t('partner_form_select')}</option>
                    <option value="до 100 кг/мес" data-id="element-1093">
                      {t('partner_form_volume_opt1')}
                    </option>
                    <option value="100–500 кг/мес" data-id="element-1094">
                      {t('partner_form_volume_opt2')}
                    </option>
                    <option value="500–1000 кг/мес" data-id="element-1095">
                      {t('partner_form_volume_opt3')}
                    </option>
                    <option value="более 1000 кг/мес" data-id="element-1096">
                      {t('partner_form_volume_opt4')}
                    </option>
                  </select>
                </div>
              </div>
              <div className="space-y-2 mb-8" data-id="element-1097">
                <label className="text-sm font-medium text-gray-700" data-id="element-1098">
                  {t('partner_form_info')}
                </label>
                <textarea name="message" rows={4} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all resize-none" placeholder={t('partner_form_placeholder_info')} data-id="element-1099" />
              </div>
              <button type="submit" disabled={loading} className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-medium uppercase tracking-wide px-8 py-4 text-base rounded-sm transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2" data-id="element-1100">
                {loading ? <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" data-id="element-1101">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1102" />

                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" data-id="element-1103" />
                    </svg>
                    {t('partner_form_sending')}
                  </> : t('contact_form_submit')}
              </button>
              {submitError && <p className="text-red-600 text-sm text-center mt-2" data-id="element-1104">
                  {t('partner_form_err')}
                </p>}
              <p className="text-xs text-center text-gray-500 mt-4" data-id="element-1105">
                {t('contact_form_policy')}
              </p>
            </form>}

          <div className="mt-10 flex flex-col sm:flex-row gap-6 justify-center text-center" data-id="element-1106">
            <a href="tel:+996502100636" className="flex items-center justify-center gap-2 text-gray-600 hover:text-red-600 transition-colors" data-id="element-1107">
              <Phone className="h-5 w-5" data-id="element-1108" />
              <span data-id="element-1109">+996 502 100 636</span>
            </a>
            <a href="https://instagram.com/yavuzburak.kg" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 text-gray-600 hover:text-red-600 transition-colors" data-id="element-1110">
              <Mail className="h-5 w-5" data-id="element-1111" />
              <span data-id="element-1112">@yavuzburak.kg</span>
            </a>
          </div>
        </div>
      </Section>
    </div>;
}